'use client';

import React, { useState, useEffect, useRef } from 'react';
import { quizDefinition, getQuizStep, QuizStep, QuizAnswer } from '../../lib/quiz-definition';
import QuestionStep from './QuestionStep';
import StatementStep from './StatementStep';
import PreCheckoutStep from './PreCheckoutStep';
import CheckoutStep from './CheckoutStep';
import LoadingStep from './LoadingStep';
import { motion, AnimatePresence } from 'framer-motion';

interface QuizContainerProps {
    isOpen: boolean;
    onClose: () => void;
    basePath: string;
}

const LOCAL_STORAGE_KEY = 'quizState';

// Type for window.atlyTracker
declare global {
    interface Window {
        atlyTracker?: {
            // Add the generic trackEvent method signature
            trackEvent: (eventName: string, properties: Record<string, any>) => boolean;
            // Keep existing specific methods
            trackSliderShown: () => void;
            trackSeeMorePlacesClick: () => void;
            trackCTAClick: (elementName: string) => void;
            trackAppDownload: (source: string) => void;
            trackItemTapped: (tappedItem: string) => void;
            getABTestUrl: () => string;

        };
    }
}

const QuizContainer: React.FC<QuizContainerProps> = ({ isOpen, onClose, basePath }) => {
    const [currentStepId, setCurrentStepId] = useState<string>('start');
    const [history, setHistory] = useState<string[]>([]); // Track navigation history for Back button
    const [answers, setAnswers] = useState<{ [key: string]: any }>({}); // Store answers
    const [isLoaded, setIsLoaded] = useState(false); // Prevent rendering until loaded from localStorage
    const [direction, setDirection] = useState(1); // 1 for forward, -1 for backward
    const touchStartX = useRef<number | null>(null); // Ref to store touch start position
    const touchEndX = useRef<number | null>(null); // Ref to store touch end position (optional, can calculate diff directly)

    // Preload quiz images when quiz becomes visible
    useEffect(() => {
        if (isOpen) {
            console.log("[Quiz Preload] Starting image preload...");
            const imageUrls = quizDefinition
                .map(step => step.imageUrl)
                .filter((url): url is string => typeof url === 'string' && url !== ''); // Filter out undefined/empty strings
            
            // Convert Set to Array explicitly
            const uniqueImageUrls = Array.from(new Set(imageUrls)); 

            console.log(`[Quiz Preload] Preloading ${uniqueImageUrls.length} images...`);
            uniqueImageUrls.forEach(url => {
                const img = new Image();
                img.src = basePath + url;
            });
        }
    }, [isOpen]); // Run only when isOpen changes (specifically when it becomes true)

    // Load state from localStorage on mount
    useEffect(() => {
        const savedState = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (savedState) {
            try {
                const { stepId, history: savedHistory, answers: savedAnswers } = JSON.parse(savedState);
                if (getQuizStep(stepId)) { // Validate stepId exists
                    setCurrentStepId(stepId);
                    setHistory(savedHistory || []);
                    setAnswers(savedAnswers || {});
                } else {
                    // Invalid step, reset
                    localStorage.removeItem(LOCAL_STORAGE_KEY);
                    setHistory([]);
                    setAnswers({});
                }
            } catch (e) {
                console.error("Error loading quiz state:", e);
                localStorage.removeItem(LOCAL_STORAGE_KEY);
            }
        }
        setIsLoaded(true);
    }, []);

    // Save state to localStorage whenever it changes
    useEffect(() => {
        if (isLoaded) { 
            const stateToSave = JSON.stringify({ stepId: currentStepId, history, answers });
            localStorage.setItem(LOCAL_STORAGE_KEY, stateToSave);


        }
    }, [currentStepId, history, answers, isLoaded]);

    // Main navigation function
    const handleNavigate = (nextStepId: string) => {
        const nextStep = getQuizStep(nextStepId);
        if (nextStep) {
            setDirection(1); // Set direction to forward
            setHistory([...history, currentStepId]);
            setCurrentStepId(nextStepId);
        } else {
            console.error(`Invalid nextStepId: ${nextStepId}`);
        }
    };

    // Specific handler for when a question is answered
    const handleAnswerQuestion = (chosenAnswer: QuizAnswer) => {
         if (currentStep && window.atlyTracker) {
             const eventProps = {
                 page_id: currentStepId, 
                 answers: currentStep.answers, 
                 choosenAnswer: chosenAnswer 
             };
             try {
                window.atlyTracker.trackEvent('tt_atly4_question_page_answered', eventProps);
             } catch (e) {
                console.error("Mixpanel track answer error:", e);
             }
         }
         // Update answers state
         setAnswers(prev => ({ ...prev, [currentStepId]: chosenAnswer.text }));
         // Navigate to the next step defined by the answer
         handleNavigate(chosenAnswer.nextStepId);
    };

    // Handler for skipping a question
    const handleSkip = (skipToStepId: string) => {
        // Add skip tracking if needed
        handleNavigate(skipToStepId); 
    };

    const handleBack = () => {
        if (history.length > 0) {
            setDirection(-1); // Set direction to backward
            const previousStepId = history[history.length - 1];
            setHistory(history.slice(0, -1)); // Remove last step from history
            setCurrentStepId(previousStepId);
        }
    };

    const handleClose = () => {
        onClose(); // Call the prop function passed from parent
    };

    const currentStep = getQuizStep(currentStepId);
    const currentStepIndex = quizDefinition.findIndex(step => step.id === currentStepId);
    const totalSteps = quizDefinition.length;
    // Calculate progress (avoid division by zero if only 1 step)
    const progress = totalSteps > 1 ? (currentStepIndex / (totalSteps - 1)) * 100 : 100;

    if (!isOpen || !isLoaded || !currentStep) {
        return null; // Don't render if closed, not loaded, or step not found
    }

    const isFirstStep = history.length === 0 && currentStepId === 'start';

    // Main content area rendering based on step type
    const renderStepContent = () => {
        switch (currentStep.type) {
            case 'question':
                return <QuestionStep 
                            step={currentStep} 
                            onAnswer={handleAnswerQuestion} 
                            onSkip={handleSkip} 
                        />;
            case 'statement':
                return <StatementStep 
                            step={currentStep} 
                            onNext={(nextId) => handleNavigate(nextId)} 
                            basePath={basePath}
                        />;
            case 'loading':
                return <LoadingStep 
                            step={currentStep} 
                            onComplete={handleNavigate}
                            currentStepId={currentStepId}
                        />;
            case 'precheckout':
                 return <PreCheckoutStep 
                             step={currentStep} 
                             onNext={handleNavigate}
                         />;
             case 'checkout':
                 return <CheckoutStep 
                             step={currentStep}
                         />;
            default:
                return <div>Unknown step type</div>;
        }
    };

    // Header title/description based on current step
    const renderHeaderContent = () => {
        switch (currentStep.type) {
            case 'question':
                return (
                    <>
                        <h1 className="quiz-header-title">{currentStep.content}</h1>
                    </>
                );
            case 'statement':
                // Use title from quiz definition for statement header
                return currentStep.title ? (
                    <h1 className="quiz-header-title">{currentStep.title}</h1>
                ) : null; // Render nothing if title is not provided
            case 'loading':
                // Keep specific title for loading
                return <h1 className="quiz-header-title">Hold Tight!</h1>; 
            case 'precheckout':
                 // Keep specific title for precheckout
                // return <h1 className="quiz-header-title">Get Ready!</h1>; 
                break;
            case 'checkout':
                 // Keep specific title for checkout
                //  return <h1 className="quiz-header-title">Checkout</h1>; 
                 break;
            default:
                // return <h1 className="quiz-header-title">Quiz</h1>;
                break;
        }
    };

    // Define animation variants
    const variants = {
        enter: (direction: number) => {
            return {
                x: direction > 0 ? '100%' : '-100%',
                opacity: 0
            };
        },
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1
        },
        exit: (direction: number) => {
            return {
                zIndex: 0,
                x: direction < 0 ? '100%' : '-100%',
                opacity: 0
            };
        }
    };

    // Define transition properties
    const transition = {
        x: { type: "spring" as const, stiffness: 100, damping: 20 },
        opacity: { duration: 0.4 }
    };

    // --- Touch Handlers for Swipe Navigation ---
    const swipeThreshold = 50; // Minimum distance in pixels to trigger swipe

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.targetTouches[0].clientX;
        touchEndX.current = null; // Reset end position on new touch
    };

    // Optional: Track touch move if more complex logic needed (e.g., prevent vertical scroll)
    // const handleTouchMove = (e: React.TouchEvent) => {
    //     if (!touchStartX.current) return;
    //     touchEndX.current = e.targetTouches[0].clientX;
    // };

    const handleTouchEnd = (e: React.TouchEvent) => {
        if (!touchStartX.current) return;

        const currentTouchEndX = e.changedTouches[0].clientX;
        const deltaX = currentTouchEndX - touchStartX.current;

        const isFirstStep = history.length === 0; 

        // Swipe Right (Back)
        if (deltaX > swipeThreshold && !isFirstStep) {
            console.log('Swipe Right detected, going back');
            handleBack();
        }
        // Swipe Left (Forward)
        else if (deltaX < -swipeThreshold) {
            console.log('Swipe Left detected, attempting to go forward');
            const currentStep = getQuizStep(currentStepId);
            if (!currentStep) return; // Should not happen if logic is sound

            let targetNextStepId: string | undefined = undefined;

            switch (currentStep.type) {
                case 'question':
                    const chosenAnswerText = answers[currentStepId];
                    if (chosenAnswerText && currentStep.answers) {
                        const answerObject = currentStep.answers.find(a => a.text === chosenAnswerText);
                        if (answerObject) {
                            targetNextStepId = answerObject.nextStepId;
                        }
                    } else {
                        // No answer saved, check if skipping is possible
                        if (currentStep.skipToStepId && getQuizStep(currentStep.skipToStepId)) {
                             console.log('Swipe Left on unanswered question, triggering skip');
                             targetNextStepId = currentStep.skipToStepId;
                             // Also need to update history correctly when skipping via swipe?
                             // For now, handleNavigate will add the current step to history.
                             // Let's assume this is okay, mimicking clicking skip button.
                        } else {
                             console.log('Swipe Left on unanswered question - skip not possible or defined, swipe disabled');
                        }
                    }
                    break;
                case 'statement':
                case 'loading':
                case 'precheckout':
                    targetNextStepId = currentStep.nextStepId;
                    break;
                case 'checkout':
                    // Cannot swipe forward from checkout step
                    console.log('Swipe Left on checkout step - swipe disabled');
                    break;
            }

            if (targetNextStepId) {
                // Check if the target step actually exists before navigating
                if (getQuizStep(targetNextStepId)) {
                     console.log(`Swipe Left navigating to: ${targetNextStepId}`);
                     handleNavigate(targetNextStepId);
                } else {
                    console.warn(`Swipe Left target step ${targetNextStepId} not found in definition.`);
                }
            } else {
                 console.log('Swipe Left - No valid forward step determined.');
            }
        }

        // Reset start position after swipe action
        touchStartX.current = null;
    };
    // --- End Touch Handlers ---

    return (
        <div className={`quiz-fullscreen-container ${isOpen ? 'visible' : ''}`}>
            {/* REMOVED Header Section */}
            {/* <header className="quiz-header"> ... </header> */}

             {/* Main Content / Options Section */} 
             <main 
                className="quiz-main-content"
                onTouchStart={handleTouchStart}
                // onTouchMove={handleTouchMove} // Optional
                onTouchEnd={handleTouchEnd}
            >
                 {/* MOVED Progress Bar and Top Buttons Here */}
                 <div className="quiz-header-top-row">
                    {/* Move Close Button to the start */}
                    {isFirstStep ? (
                         <button onClick={handleClose} className="quiz-close-header-btn">✕</button>
                    ) : (
                         <button onClick={handleBack} className="quiz-back-header-btn">← Back</button>
                    )}
                    {/* Skip button or placeholder on the right */}
                     {currentStep.skipToStepId && !isFirstStep ? (
                         <button onClick={() => handleSkip(currentStep.skipToStepId!)} className="quiz-skip-header-btn">Skip</button>
                     ) : (
                         <div style={{ minWidth: '50px' }} /> /* Keep placeholder for alignment */
                     )}
                 </div>
                 <div className="quiz-progress-bar-container">
                     <div className="quiz-progress-bar-fill" style={{ width: `${progress}%` }}></div>
                 </div>


                 {/* Renders the actual step content */}
                 <div style={{ position: 'relative', overflow: 'visible', flexGrow: 1, display: 'flex', flexDirection: 'column' }}> {/* Changed overflow to visible */}
                     <AnimatePresence initial={false} custom={direction}>
                         <motion.div
                            key={currentStepId} // Important for AnimatePresence
                            custom={direction}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={transition}
                            style={{ position: 'absolute', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto' }} // Added overflowY: 'auto'
                         >
                            {/* Title and Description - MOVED INSIDE */} 

                            {/* Renders the actual step content */} 
                            {renderStepContent()}
                         </motion.div>
                     </AnimatePresence>
                 </div>
             </main>


         </div>
    );
};

export default QuizContainer; 